import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Droplets, TrendingUp, DollarSign, Users, AlertCircle, ExternalLink, Wallet } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ethers } from "ethers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Contract addresses on Polygon
const POLYGON_CHAIN_ID = '0x89'; // 137 in hex
const USDT_ADDRESS = '0xc2132D05D31c914a87C6611C10748AEb04B58e8F';

function LiquidityPool() {
  const { user } = useContext(AuthContext);
  const [poolStats, setPoolStats] = useState(null);
  const [userBalance, setUserBalance] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawShares, setWithdrawShares] = useState("");
  const [loading, setLoading] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  useEffect(() => {
    fetchData();
    checkWalletConnection();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchPoolStats(), fetchUserBalance()]);
  };

  const fetchPoolStats = async () => {
    try {
      const response = await axios.get(`${API}/pool/stats`);
      setPoolStats(response.data);
    } catch (error) {
      console.error("Error fetching pool stats:", error);
    }
  };

  const fetchUserBalance = async () => {
    try {
      const response = await axios.get(`${API}/pool/user-balance`);
      setUserBalance(response.data);
    } catch (error) {
      console.error("Error fetching user balance:", error);
    }
  };

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.listAccounts();
        
        if (accounts.length > 0) {
          setWalletConnected(true);
          setWalletAddress(accounts[0]);
          setProvider(provider);
          setSigner(provider.getSigner());
          
          // Link wallet to backend
          await axios.post(`${API}/pool/link-wallet?wallet_address=${accounts[0]}`);
        }
      } catch (error) {
        console.error("Error checking wallet:", error);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      toast.error("Please install MetaMask to use this feature!");
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    try {
      setLoading(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      
      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Check if on Polygon network
      const network = await provider.getNetwork();
      if (network.chainId !== 137) {
        // Switch to Polygon
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: POLYGON_CHAIN_ID }],
          });
        } catch (switchError) {
          // Add Polygon network if not added
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: POLYGON_CHAIN_ID,
                chainName: 'Polygon Mainnet',
                nativeCurrency: {
                  name: 'MATIC',
                  symbol: 'MATIC',
                  decimals: 18
                },
                rpcUrls: ['https://polygon-rpc.com'],
                blockExplorerUrls: ['https://polygonscan.com/']
              }]
            });
          }
        }
      }

      const signer = provider.getSigner();
      const address = await signer.getAddress();
      
      setWalletConnected(true);
      setWalletAddress(address);
      setProvider(provider);
      setSigner(signer);
      
      // Link wallet to backend
      await axios.post(`${API}/pool/link-wallet?wallet_address=${address}`);
      
      toast.success("Wallet connected successfully!");
      await fetchUserBalance();
    } catch (error) {
      console.error("Error connecting wallet:", error);
      toast.error("Failed to connect wallet");
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error("Please enter a valid deposit amount");
      return;
    }

    if (!walletConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/pool/deposit`, null, {
        params: {
          amount: parseFloat(depositAmount)
        }
      });
      
      if (response.data.success) {
        toast.success(
          <div>
            <div className="font-semibold">Deposit Successful!</div>
            <div className="text-sm">Amount: {depositAmount} USDC</div>
            <div className="text-sm">Shares: {response.data.shares_received}</div>
          </div>
        );
        setDepositAmount("");
        await fetchData(); // Refresh data
      }
    } catch (error) {
      console.error("Deposit error:", error);
      toast.error(error.response?.data?.detail || "Deposit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    
    if (!withdrawShares || parseFloat(withdrawShares) <= 0) {
      toast.error("Please enter a valid number of shares to withdraw");
      return;
    }

    if (!walletConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/pool/withdraw`, null, {
        params: {
          shares: parseFloat(withdrawShares)
        }
      });
      
      if (response.data.success) {
        toast.success(
          <div>
            <div className="font-semibold">Withdrawal Successful!</div>
            <div className="text-sm">Shares: {withdrawShares}</div>
            <div className="text-sm">Amount: {response.data.amount_received} USDC</div>
          </div>
        );
        setWithdrawShares("");
        await fetchData(); // Refresh data
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    } finally {
      setLoading(false);
    }
  };

  if (!poolStats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="liquidity-pool-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2 flex items-center">
            <Droplets className="w-8 h-8 mr-3 text-cyan-400" />
            Liquidity Pool
          </h1>
          <p className="text-slate-400">Provide liquidity and earn from options trading fees</p>
        </div>

        {/* Blockchain Notice */}
        {poolStats.is_mock && (
          <Card className="bg-amber-900/20 border-amber-800/50 p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-semibold mb-1">Demo Mode</p>
                <p>Showing demo data. Deploy the smart contract to Polygon to enable real blockchain functionality.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Wallet Connection */}
        {!walletConnected && (
          <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-cyan-800/50 p-6">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-3">
                <Wallet className="w-12 h-12 text-cyan-400" />
                <div>
                  <h3 className="text-xl font-bold text-slate-100">Connect Wallet</h3>
                  <p className="text-slate-400 text-sm">Connect your MetaMask wallet to interact with the liquidity pool</p>
                </div>
              </div>
              <Button
                onClick={connectWallet}
                disabled={loading}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0"
                data-testid="connect-wallet-button"
              >
                {loading ? "Connecting..." : "Connect MetaMask"}
              </Button>
            </div>
          </Card>
        )}

        {/* Wallet Info */}
        {walletConnected && (
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-slate-300 text-sm">Wallet Connected</span>
              </div>
              <span className="text-cyan-400 text-sm font-mono">
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </span>
            </div>
          </Card>
        )}

        {/* Pool Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-cyan-800/50 p-6" data-testid="total-liquidity-card">
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-8 h-8 text-cyan-400" />
              <div>
                <p className="text-slate-400 text-sm">Total Liquidity</p>
                <p className="text-2xl font-bold text-cyan-400">
                  ${poolStats?.total_liquidity?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-2">
              <TrendingUp className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-slate-400 text-sm">APY</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {poolStats.apy}%
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Users className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-slate-400 text-sm">Total Shares</p>
                <p className="text-2xl font-bold text-slate-100">
                  {poolStats?.total_shares?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-slate-400 text-sm">Net Profit</p>
                <p className="text-2xl font-bold text-purple-400">
                  ${poolStats?.net_profit?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* User Balance & Actions */}
        {walletConnected && userBalance && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Your Position */}
            <Card className="bg-slate-800/50 border-slate-700 p-8">
              <h2 className="text-2xl font-bold text-slate-100 mb-6">Your Position</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                  <span className="text-slate-400">Your Liquidity</span>
                  <span className="text-2xl font-bold text-cyan-400">
                    ${userBalance?.current_value?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                  <span className="text-slate-400">Your Shares</span>
                  <span className="text-xl font-semibold text-slate-100">
                    {userBalance?.shares?.toLocaleString(undefined, {maximumFractionDigits: 2}) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Pool Share</span>
                  <span className="text-lg font-semibold text-slate-100">
                    {poolStats.total_shares > 0 ? ((userBalance.shares / poolStats.total_shares) * 100).toFixed(2) : 0}%
                  </span>
                </div>
              </div>
            </Card>

            {/* Deposit */}
            <Card className="bg-slate-800/50 border-slate-700 p-8">
              <h2 className="text-2xl font-bold text-slate-100 mb-6">Add Liquidity</h2>
              <form onSubmit={handleDeposit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-slate-300">Amount (USDT)</Label>
                  <Input
                    type="number"
                    step="any"
                    min="1"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="bg-slate-700 border-slate-600 text-slate-100"
                    data-testid="deposit-input"
                  />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setDepositAmount("100")} className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-300">
                      $100
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setDepositAmount("500")} className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-300">
                      $500
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setDepositAmount("1000")} className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-300">
                      $1000
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !depositAmount}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white border-0"
                  data-testid="deposit-button"
                >
                  {loading ? "Processing..." : "Deposit to Pool"}
                </Button>
              </form>

              {/* Withdraw */}
              <div className="mt-8 pt-8 border-t border-slate-700">
                <h3 className="text-xl font-bold text-slate-100 mb-4">Withdraw</h3>
                <form onSubmit={handleWithdraw} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Shares to Withdraw</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      max={userBalance.shares}
                      value={withdrawShares}
                      onChange={(e) => setWithdrawShares(e.target.value)}
                      placeholder="Enter shares"
                      className="bg-slate-700 border-slate-600 text-slate-100"
                      data-testid="withdraw-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !withdrawShares}
                    variant="outline"
                    className="w-full bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200"
                    data-testid="withdraw-button"
                  >
                    {loading ? "Processing..." : "Withdraw from Pool"}
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        )}

        {/* How It Works */}
        <Card className="bg-slate-800/50 border-slate-700 p-8">
          <h2 className="text-2xl font-bold text-slate-100 mb-6">How Liquidity Pool Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                <Droplets className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-100 mb-2">1. Deposit USDT</h3>
              <p className="text-slate-400 text-sm">Add USDT to the liquidity pool and receive LP shares representing your portion of the pool.</p>
            </div>
            
            <div>
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-100 mb-2">2. Earn from Trades</h3>
              <p className="text-slate-400 text-sm">When traders lose options, their stakes go to the pool. When they win, payouts come from the pool.</p>
            </div>
            
            <div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-100 mb-2">3. Withdraw Anytime</h3>
              <p className="text-slate-400 text-sm">Redeem your shares for USDT plus your proportional share of profits earned by the pool.</p>
            </div>
          </div>
        </Card>

        {/* Smart Contract Link */}
        <Card className="bg-slate-800/50 border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-100 mb-1">Smart Contract</h3>
              <p className="text-slate-400 text-sm">View contract on PolygonScan</p>
            </div>
            <Button
              variant="outline"
              className="bg-slate-700 hover:bg-slate-600 border-slate-600"
              onClick={() => {
                const contractAddress = poolStats?.contract_address;
                if (contractAddress) {
                  window.open(`https://polygonscan.com/address/${contractAddress}`, '_blank');
                } else {
                  toast.error("Contract address not available");
                }
              }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Contract
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default LiquidityPool;
